import { Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

type DetailRowProps = {
  icon?: LucideIcon;
  label: string;
  value: string;
  highlighted?: boolean;
};

export function DetailRow({
  icon: Icon,
  label,
  value,
  highlighted = false,
}: DetailRowProps) {
  return (
    <div
      className={cn(
        "rounded-lg border px-3 py-2.5",
        highlighted
          ? "bg-ds-blue-50/50 border-ds-blue-100"
          : "bg-ds-surface-50/50 border-ds-border-200"
      )}
    >
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && (
          <Icon
            size={13}
            className={cn(highlighted ? "text-ds-blue-600" : "text-ds-text-400")}
          />
        )}
        <span className="text-[11px] text-ds-text-500 uppercase tracking-wide">
          {label}
        </span>
      </div>
      <p
        className={cn(
          "text-sm font-bold font-heading",
          highlighted ? "text-ds-blue-800" : "text-navy-900"
        )}
      >
        {value}
      </p>
    </div>
  );
}

export function DetailGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-2">{children}</div>;
}

type SectionHeadingProps = {
  children: ReactNode;
  to?: string;
  onClick?: () => void;
};

export function SectionHeading({ children, to, onClick }: SectionHeadingProps) {
  return (
    <div className="flex items-center justify-between mt-5 mb-3 first:mt-0">
      <div className="flex items-center gap-2">
        <div className="w-1 h-4 rounded-full bg-ds-blue-600" />
        <h4 className="text-xs font-bold font-heading uppercase tracking-wider text-ds-blue-800">
          {children}
        </h4>
      </div>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className="text-xs text-ds-blue-600 hover:underline shrink-0"
        >
          Detalii &rarr;
        </button>
      ) : (
        to && (
          <Link
            to={to}
            className="text-xs text-ds-blue-600 hover:underline shrink-0"
          >
            Detalii &rarr;
          </Link>
        )
      )}
    </div>
  );
}

type ComingSoonPlaceholderProps = {
  title?: string;
  message?: string;
};

export function ComingSoonPlaceholder({
  title = "In curand",
  message = "Disponibil dupa finalizarea etapelor anterioare.",
}: ComingSoonPlaceholderProps) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center rounded-lg bg-gradient-to-b from-ds-surface-50 to-ds-surface-100/50 border border-dashed border-ds-border-200">
      <div className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center mb-3">
        <Clock size={20} className="text-ds-text-400" />
      </div>
      <p className="text-sm font-semibold font-heading text-ds-text-600">{title}</p>
      <p className="text-xs text-ds-text-400 mt-1.5 max-w-[250px]">{message}</p>
    </div>
  );
}
